import type { LucideIcon } from 'lucide-vue-next';
import {
    Code2,
    Crop,
    Eraser,
    FileArchive,
    FileMinus,
    FileSpreadsheet,
    FileText,
    Files,
    GitCompareArrows,
    Hash,
    Image,
    ImageDown,
    Layers,
    ListOrdered,
    Lock,
    LockOpen,
    PenLine,
    Presentation,
    RotateCw,
    ScanText,
    ScissorsLineDashed,
    Stamp,
    Table2,
    Wrench,
} from 'lucide-vue-next';
import '@/lib/ensureWayfinderLocale';
import { toUrl } from '@/lib/utils';
import {
    addPageNumbers,
    addWatermark,
    compare,
    compress,
    crop,
    extractPages,
    fromExcel,
    fromHtml,
    fromJpg,
    fromPowerpoint,
    fromWord,
    merge,
    ocr,
    protect,
    redact,
    removePages,
    reorder,
    repair,
    rotate,
    sign,
    split,
    toExcel,
    toJpg,
    toPowerpoint,
    toWord,
    unlock,
} from '@/routes/tools/pdf';

export type PdfMegaMenuItem = {
    key: string;
    title: string;
    href: string;
    description: string;
    icon: LucideIcon;
    iconClass: string;
};

/** True when the current path belongs to one of this section's tools (not every /tools route). */
export function isPdfNavSectionActive(
    currentPath: string,
    items: PdfMegaMenuItem[],
): boolean {
    return items.some((item) => {
        const raw = toUrl(item.href);
        const base = raw.split('?')[0] ?? raw;

        return currentPath === base || currentPath.startsWith(`${base}/`);
    });
}

export const convertToPdfNavItems: PdfMegaMenuItem[] = [
    {
        key: 'from_jpg',
        title: 'JPG / PNG to PDF',
        href: fromJpg.url(),
        description: 'Create PDF from images',
        icon: Image,
        iconClass: 'text-cyan-700 dark:text-cyan-300',
    },
    {
        key: 'from_word',
        title: 'Word to PDF',
        href: fromWord.url(),
        description: 'Convert .docx to PDF',
        icon: FileText,
        iconClass: 'text-blue-600 dark:text-blue-300',
    },
    {
        key: 'from_powerpoint',
        title: 'PowerPoint to PDF',
        href: fromPowerpoint.url(),
        description: 'Convert .pptx to PDF',
        icon: Presentation,
        iconClass: 'text-orange-600 dark:text-orange-300',
    },
    {
        key: 'from_excel',
        title: 'Excel to PDF',
        href: fromExcel.url(),
        description: 'Convert .xlsx to PDF',
        icon: Table2,
        iconClass: 'text-emerald-600 dark:text-emerald-300',
    },
    {
        key: 'from_html',
        title: 'HTML to PDF',
        href: fromHtml.url(),
        description: 'Render HTML as PDF',
        icon: Code2,
        iconClass: 'text-brand-600 dark:text-brand-300',
    },
];

export const organizePdfNavItems: PdfMegaMenuItem[] = [
    {
        key: 'merge',
        title: 'Merge PDF',
        href: merge.url(),
        description: 'Combine multiple PDFs',
        icon: Layers,
        iconClass: 'text-red-600 dark:text-red-300',
    },
    {
        key: 'split',
        title: 'Split PDF',
        href: split.url(),
        description: 'Split into separate files',
        icon: ScissorsLineDashed,
        iconClass: 'text-red-600 dark:text-red-300',
    },
    {
        key: 'remove_pages',
        title: 'Remove Pages',
        href: removePages.url(),
        description: 'Delete specific pages',
        icon: FileMinus,
        iconClass: 'text-red-600 dark:text-red-300',
    },
    {
        key: 'extract_pages',
        title: 'Extract Pages',
        href: extractPages.url(),
        description: 'Pull out specific pages',
        icon: Files,
        iconClass: 'text-red-600 dark:text-red-300',
    },
    {
        key: 'reorder',
        title: 'Reorder Pages',
        href: reorder.url(),
        description: 'Change page order',
        icon: ListOrdered,
        iconClass: 'text-red-600 dark:text-red-300',
    },
    {
        key: 'compress',
        title: 'Compress PDF',
        href: compress.url(),
        description: 'Reduce file size',
        icon: FileArchive,
        iconClass: 'text-red-600 dark:text-red-300',
    },
    {
        key: 'repair',
        title: 'Repair PDF',
        href: repair.url(),
        description: 'Fix corrupted PDFs',
        icon: Wrench,
        iconClass: 'text-red-600 dark:text-red-300',
    },
];

export const editPdfNavItems: PdfMegaMenuItem[] = [
    {
        key: 'rotate',
        title: 'Rotate PDF',
        href: rotate.url(),
        description: 'Rotate all pages',
        icon: RotateCw,
        iconClass: 'text-slate-600 dark:text-slate-300',
    },
    {
        key: 'add_page_numbers',
        title: 'Add Page Numbers',
        href: addPageNumbers.url(),
        description: 'Number each page automatically',
        icon: Hash,
        iconClass: 'text-slate-600 dark:text-slate-300',
    },
    {
        key: 'add_watermark',
        title: 'Add Watermark',
        href: addWatermark.url(),
        description: 'Overlay text watermark',
        icon: Stamp,
        iconClass: 'text-slate-600 dark:text-slate-300',
    },
    {
        key: 'crop',
        title: 'Crop PDF',
        href: crop.url(),
        description: 'Trim page margins',
        icon: Crop,
        iconClass: 'text-slate-600 dark:text-slate-300',
    },
    {
        key: 'ocr',
        title: 'OCR PDF',
        href: ocr.url(),
        description: 'Make scans searchable',
        icon: ScanText,
        iconClass: 'text-slate-600 dark:text-slate-300',
    },
];

export const securityPdfNavItems: PdfMegaMenuItem[] = [
    {
        key: 'unlock',
        title: 'Unlock PDF',
        href: unlock.url(),
        description: 'Remove password protection',
        icon: LockOpen,
        iconClass: 'text-amber-600 dark:text-amber-300',
    },
    {
        key: 'protect',
        title: 'Protect PDF',
        href: protect.url(),
        description: 'Encrypt with a password',
        icon: Lock,
        iconClass: 'text-amber-600 dark:text-amber-300',
    },
    {
        key: 'sign',
        title: 'Sign PDF',
        href: sign.url(),
        description: 'Draw or upload a signature',
        icon: PenLine,
        iconClass: 'text-amber-600 dark:text-amber-300',
    },
    {
        key: 'redact',
        title: 'Redact PDF',
        href: redact.url(),
        description: 'Black out selected pages',
        icon: Eraser,
        iconClass: 'text-amber-600 dark:text-amber-300',
    },
    {
        key: 'compare',
        title: 'Compare PDF',
        href: compare.url(),
        description: 'Diff text between two PDFs',
        icon: GitCompareArrows,
        iconClass: 'text-amber-600 dark:text-amber-300',
    },
];

export const convertFromPdfNavItems: PdfMegaMenuItem[] = [
    {
        key: 'to_jpg',
        title: 'PDF to JPG',
        href: toJpg.url(),
        description: 'Convert pages to images',
        icon: ImageDown,
        iconClass: 'text-cyan-700 dark:text-cyan-300',
    },
    {
        key: 'to_word',
        title: 'PDF to Word',
        href: toWord.url(),
        description: 'Export as .docx',
        icon: FileText,
        iconClass: 'text-blue-600 dark:text-blue-300',
    },
    {
        key: 'to_powerpoint',
        title: 'PDF to PowerPoint',
        href: toPowerpoint.url(),
        description: 'Export as .pptx',
        icon: Presentation,
        iconClass: 'text-orange-600 dark:text-orange-300',
    },
    {
        key: 'to_excel',
        title: 'PDF to Excel',
        href: toExcel.url(),
        description: 'Export as .xlsx',
        icon: FileSpreadsheet,
        iconClass: 'text-emerald-600 dark:text-emerald-300',
    },
];

export const allPdfNavItems: PdfMegaMenuItem[] = [
    ...convertToPdfNavItems,
    ...organizePdfNavItems,
    ...editPdfNavItems,
    ...securityPdfNavItems,
    ...convertFromPdfNavItems,
];
